import { log } from "@/core/logger";
import { fetchWithAuth } from "@/utils/fetchWithAuth";
import { io, Socket } from "socket.io-client";
import { API_BASE_URL } from "./helpers";
import type { IMessageAPI } from "./messageApi";
import type { Conversation, Message } from "@/types/Message";

class RemoteMessageAPI implements IMessageAPI {
  private socket: Socket | null = null;
  private apiUrl: string;
  private eventListeners: {
    newMessage: ((message: Message) => void)[];
    messageRead: ((data: { messageId: string; readAt: Date }) => void)[];
    conversationRead: ((data: { conversationId: string; memberId: string }) => void)[];
    userOnline: ((memberId: string) => void)[];
    userOffline: ((memberId: string) => void)[];
  } = {
    newMessage: [],
    messageRead: [],
    conversationRead: [],
    userOnline: [],
    userOffline: [],
  };

  constructor() {
    this.apiUrl = API_BASE_URL;
  }

  // ============================================
  // REAL-TIME WEBSOCKET
  // ============================================
  async connect(): Promise<void> {
    // If already connected, no-op
    if (this.socket?.connected) {
      return;
    }

    // If a socket exists but is disconnected, clean it up
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }

    this.socket = io(this.apiUrl, {
      transports: ["websocket", "polling"],
      withCredentials: true,
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
      timeout: 10000,
    });

    // Register event listeners once
    this.socket.on("new_message", (message: Message) => {
      log("remoteMessageApi", "Received new_message event:", message);
      this.eventListeners.newMessage.forEach((callback) => callback(message));
    });

    this.socket.on("message_read", (data: { messageId: string; readAt: Date }) => {
      this.eventListeners.messageRead.forEach((callback) => callback(data));
    });

    this.socket.on("conversation_read", (data: { conversationId: string; memberId: string }) => {
      this.eventListeners.conversationRead.forEach((callback) => callback(data));
    });

    this.socket.on("user_online", (memberId: string) => {
      this.eventListeners.userOnline.forEach((callback) => callback(memberId));
    });

    this.socket.on("user_offline", (memberId: string) => {
      this.eventListeners.userOffline.forEach((callback) => callback(memberId));
    });

    this.socket.on("connect_error", (error) => {
      log("remoteMessageApi", "WebSocket connection error (will retry):", error.message);
    });

    this.socket.on("error", (error) => {
      log("remoteMessageApi", "WebSocket error:", error);
    });

    // Wait for initial connection, but with a timeout so we don't block forever.
    // If it fails, we resolve anyway — Socket.IO will keep reconnecting in the
    // background, and REST API calls can still work independently.
    return new Promise<void>((resolve) => {
      const timeout = setTimeout(() => {
        log("remoteMessageApi", "WebSocket initial connection timed out — continuing with REST, socket will reconnect in background");
        resolve();
      }, 5000);

      this.socket!.once("connect", () => {
        clearTimeout(timeout);
        log("remoteMessageApi", "WebSocket connected:", this.apiUrl);
        resolve();
      });
    });
  }

  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  onReconnect(callback: () => void): void {
    if (this.socket) {
      this.socket.on("connect", callback);
    }
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  onNewMessage(callback: (message: Message) => void): void {
    this.eventListeners.newMessage.push(callback);
  }

  onMessageRead(callback: (data: { messageId: string; readAt: Date }) => void): void {
    this.eventListeners.messageRead.push(callback);
  }

  onConversationRead(callback: (data: { conversationId: string; memberId: string }) => void): void {
    this.eventListeners.conversationRead.push(callback);
  }

  onUserOnline(callback: (memberId: string) => void): void {
    this.eventListeners.userOnline.push(callback);
  }

  onUserOffline(callback: (memberId: string) => void): void {
    this.eventListeners.userOffline.push(callback);
  }

  // ============================================
  // PERSISTENCE VIA REST API
  // ============================================
  async sendMessage(recipientId: string, content: string): Promise<Message> {
    log("remoteMessageApi.sendMessage", recipientId, `Sending message to ${recipientId}...`);
    const response = await fetchWithAuth(`${API_BASE_URL}/api/messages`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({ recipientId, content }),
    });

    if (!response.ok) {
      throw new Error("Failed to send message");
    }

    return response.json();
  }

  async sendGroupMessage(locationIds: string[], content: string): Promise<Message> {
    log("remoteMessageApi.sendGroupMessage", "group", `Sending group message to ${locationIds.length} locations...`);
    const response = await fetchWithAuth(`${API_BASE_URL}/api/messages`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({
        locationIds,
        content,
        isGroupMessage: true,
      }),
    });

    if (!response.ok) {
      throw new Error("Failed to send group message");
    }

    return response.json();
  }

  async requestLocationAccess(
    locationId: string,
    content: string,
  ): Promise<{ conversation: Conversation | null; message: Message | null }> {
    log("remoteMessageApi.requestLocationAccess", locationId, `Requesting access to location ${locationId}...`);
    const response = await fetchWithAuth(`${API_BASE_URL}/api/messages/location-request`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({ locationId, content }),
    });

    if (!response.ok) {
      // Surface the server's reason verbatim where possible — the server returns 404
      // for "no PMs at this location" so the UI can hint at fallback ("contact us").
      let message = "Failed to send location access request";
      try {
        const body = await response.json();
        if (body && typeof body.error === "string") message = body.error;
      } catch {
        /* response not JSON; keep default */
      }
      throw new Error(message);
    }

    return response.json();
  }

  async sendMessageToConversation(conversationId: string, content: string): Promise<Message> {
    log("remoteMessageApi.sendMessageToConversation", conversationId, `Sending message to conversation ${conversationId}...`);
    const response = await fetchWithAuth(`${API_BASE_URL}/api/messages`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({
        conversationId,
        content,
      }),
    });

    if (!response.ok) {
      throw new Error("Failed to send message to conversation");
    }

    return response.json();
  }

  async getConversations(memberId: string): Promise<Conversation[]> {
    log("remoteMessageApi.getConversations", memberId, "Fetching conversations...");
    const response = await fetchWithAuth(`${API_BASE_URL}/api/messages/conversations`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error("Failed to fetch conversations");
    }

    return response.json();
  }

  async getConversationMessages(conversationId: string, limit: number = 50): Promise<Message[]> {
    log(
      "remoteMessageApi.getConversationMessages",
      conversationId,
      `Fetching messages for conversation ${conversationId}...`,
    );
    const response = await fetchWithAuth(`${API_BASE_URL}/api/messages/conversation/${conversationId}?limit=${limit}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error("Failed to fetch messages");
    }

    return response.json();
  }

  async getUnreadMessages(memberId: string): Promise<Message[]> {
    log("remoteMessageApi.getUnreadMessages", memberId, "Fetching unread messages...");
    const response = await fetchWithAuth(`${API_BASE_URL}/api/messages/unread`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error("Failed to fetch unread messages");
    }

    return response.json();
  }

  async markAsRead(messageId: string, memberId: string): Promise<void> {
    log("remoteMessageApi.markAsRead", memberId, `Marking message ${messageId} as read...`);
    const response = await fetchWithAuth(`${API_BASE_URL}/api/messages/${messageId}/read`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include", // Session cookie contains auth info, no need for x-member-id header
    });

    if (!response.ok) {
      throw new Error("Failed to mark message as read");
    }
  }

  async markConversationRead(conversationId: string): Promise<void> {
    log("remoteMessageApi.markConversationRead", conversationId, `Marking conversation ${conversationId} as read...`);
    const response = await fetchWithAuth(`${API_BASE_URL}/api/messages/conversations/${conversationId}/read`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error("Failed to mark conversation as read");
    }
  }
}

export default RemoteMessageAPI;
